import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { parseBasicInfo } from "../VenuesList";

/**
 * Mapa {id -> venue} para las vistas previas del blog (editor y modal).
 *
 * Mismo criterio de visibilidad que el backend GET /venues
 * (isActive && isActiveAdmin) y mismo parseo de basicInfo, para que lo que ve
 * el autor en la vista previa sea exactamente lo que el sitio va a renderizar.
 * Un venue despublicado no entra al mapa y la vista previa lo marca como id
 * inexistente — que es justo el aviso que hace falta antes de publicar.
 *
 * `enabled` evita la lectura mientras el editor/modal está cerrado.
 */

// Espejo de _RECORRIDO_TIPOS_EMBEBIBLES / _es_recorrido_embebible en
// backend/src/routes/venues.py: el badge 3D solo se muestra si el tour se
// puede embeber de verdad. La forma legacy (string con una URL) no cuenta.
const TIPOS_RECORRIDO_EMBEBIBLE = ["matterport", "kuula"];

function tieneRecorridoEmbebible(recorrido) {
  if (!recorrido || typeof recorrido !== "object") return false;
  return Boolean(recorrido.id) && TIPOS_RECORRIDO_EMBEBIBLE.includes(recorrido.tipo);
}

export default function useVenuesById(enabled = true) {
  const [venuesById, setVenuesById] = useState({});

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "venues"),
            where("isActive", "==", true),
            where("isActiveAdmin", "==", true)
          )
        );
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const info = parseBasicInfo(data.basicInfo);
          map[d.id] = {
            id: d.id,
            name: data.name || d.id,
            images: Array.isArray(data.images) ? data.images : [],
            zone: info.zone,
            capacity: info.capacity,
            // `Tipo` a veces es string y a veces array (igual que en VenuesList).
            type: Array.isArray(info.type) ? info.type : info.type ? [info.type] : [],
            hasRecorrido: tieneRecorridoEmbebible(data.recorrido),
          };
        });
        setVenuesById(map);
      } catch (e) {
        // Sin venues la vista previa marca todos los ids como inexistentes;
        // preferible a tumbar el editor.
        console.error("No se pudieron cargar los venues para la vista previa", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return venuesById;
}
